import type { Hub } from "@portal/api/types";
import { createStore } from "zustand/vanilla";
import axios from "@portal/lib/axios";
import { z } from "zod";

import { ActiveHub } from "./ActiveHub";
import type { ActiveHubOptions } from "./ActiveHub";

export type CustomerState = {
  activeHub: Nullable<ActiveHub>;
  isLoadingActiveHub: boolean;
};

export type CustomerActions = {
  reset: () => void;
  updateActiveHub: (hubs: Hub[], opts?: Partial<ActiveHubOptions>) => void;
  setInitialState: ({
    hubs,
    shouldShowAllHubsOption,
  }: {
    hubs: Hub[];
    shouldShowAllHubsOption: boolean;
  }) => void;
};

export type CustomerStore = CustomerState & CustomerActions;

export const defaultCustomerState: CustomerState = {
  activeHub: null,
  isLoadingActiveHub: true,
};

export const initCustomerStore = (): CustomerState => {
  return defaultCustomerState;
};

export const createCustomerStore = (
  initState: CustomerState = defaultCustomerState,
) => {
  return createStore<CustomerStore>()((set) => ({
    ...initState,
    reset: () => set(defaultCustomerState),
    updateActiveHub: (hubs: Hub[], opts?: Partial<ActiveHubOptions>) => {
      const activeHub = new ActiveHub(hubs, opts);
      runActiveHubSideEffects(activeHub);
      set({ activeHub: activeHub });
    },

    setInitialState: ({
      hubs,
      shouldShowAllHubsOption,
    }: Parameters<CustomerActions["setInitialState"]>[0]) => {
      // No authorized/available hubs — leave activeHub null (its nullable
      // contract) instead of constructing an ActiveHub from an empty list,
      // which would throw on hubs[0].
      if (hubs.length === 0) {
        set({ activeHub: null, isLoadingActiveHub: false });
        return;
      }

      const storedHubScope = getStoredHubScope();

      const storedDefaultHubs = getStoredDefaultHubs(hubs);
      const activeHub = new ActiveHub(storedDefaultHubs ?? hubs, {
        scope: !shouldShowAllHubsOption ? "single" : storedHubScope,
      });
      runActiveHubSideEffects(activeHub);
      set({
        activeHub: activeHub,
        isLoadingActiveHub: false,
      });
    },
  }));
};

const localStorageKeyActiveHubId = "activeHubId";
const localStorageKeyActiveHubScope = "activeHubScope";

function getStoredDefaultHubs(hubs: Hub[]) {
  try {
    const defaultHubIds = getStoreHubIdsFromLocalStorage();
    if (!defaultHubIds) return undefined;
    const filtered = hubs.filter((hub) => defaultHubIds.includes(hub.id));
    // If none of the stored ids resolve against the current hubs (hub removed,
    // re-ided, deauthorized, or customer switched), fall back to all hubs
    // rather than handing ActiveHub an empty list and crashing.
    return filtered.length > 0 ? filtered : undefined;
  } catch {
    return undefined;
  }
}

function runActiveHubSideEffects(activeHub: ActiveHub) {
  axios.defaults.headers.common["Grpc-Metadata-X-Active-Hub"] =
    activeHub.xActiveHub;
  setStoreHubIdsInLocalStorage(activeHub.id);
  localStorage.setItem("activeHubScope", activeHub.scope);
}

const scopeSchema = z.enum(["all", "single"]);
function getStoredHubScope() {
  const storedScopeRaw = localStorage.getItem(localStorageKeyActiveHubScope);
  const parsed = scopeSchema.safeParse(storedScopeRaw);
  return parsed.success ? parsed.data : "single";
}

function setStoreHubIdsInLocalStorage(hubIds: string[]) {
  try {
    localStorage.setItem(localStorageKeyActiveHubId, JSON.stringify(hubIds));
  } catch (error) {
    console.error("Error setting active hub id in localStorage", error);
  }
}

const activeHubIdSchema = z.array(z.string());
export function getStoreHubIdsFromLocalStorage(): string[] | null {
  const raw = localStorage.getItem(localStorageKeyActiveHubId);
  if (raw === null) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  const result = activeHubIdSchema.safeParse(parsed);
  if (!result.success) {
    return null;
  }

  return result.data;
}
